<script setup lang="ts">
import { LazyTooltip } from '@/components/ui/tooltip';
import FilterDialog from '@/components/FilterBuilder/FilterDialog.vue';
import TableHeader from '@/components/DataTable/TableHeader.vue';
import TableHeaderItem from '@/components/DataTable/TableHeaderItem.vue';
import TableHeaderPaginationGroup from '@/components/DataTable/TableHeaderPaginationGroup.vue';
import HistoryButton from '@/components/HistoryInspector/HistoryButton.vue';
import { usePersistentTable } from '@/composables/usePersistentTable';
import ListDisplayMenu from '@/components/DataTable/ListDisplayMenu.vue';
import { Button } from '@/components/ui/button';
import { computed } from 'vue';
import type { TableColumnStaticProps } from '@/types/tableTypes';
import type { IScope } from '@/types/filterBuilderTypes';


const props = defineProps<{
  rowsToProcess?: number;
  /** When true, Save is disabled (e.g. pending edits on a live track in Processing). */
  saveDisabled?: boolean;
  saveDisabledTooltip?: string;
  totalRecords?: number;
  /** 1-based current page for the cursor-based pager label. */
  currentPage?: number;
  /** Cursor navigation gates + in-flight flag, forwarded to the pager. */
  hasNext?: boolean;
  hasPrev?: boolean;
  loading?: boolean;
  additionalColumns?: TableColumnStaticProps[];
}>();

const emit = defineEmits<{
  'processRows': [void];
  'navigate': [direction: 'first' | 'prev' | 'next' | 'last'];
  'applyFilter': [parsedQuery: IScope];
}>();

const { storeKey, visiblePinnedColumns, visibleNonPinnedColumns } = usePersistentTable();
const visibleColumnCount = computed(() => visiblePinnedColumns.value.length + visibleNonPinnedColumns.value.length);

function applyFilterHandler(parsedQuery: IScope) {
  emit('applyFilter', parsedQuery);
}

// This page's saved views live in the section sidebar, under Music, as a
// list's do. The toolbar has no saved-view control. Opening a view goes
// through the address, and TrackMatchingPage guards it against losing
// pending matches.
</script>

<template>
  <TableHeader>
    <template #left>
      <TableHeaderItem>
        <FilterDialog
          :key="storeKey"
          fullscreen
          @applyFilter="applyFilterHandler"
        />
      </TableHeaderItem>
      <!-- Display holds "Columns…", as the list toolbar's does. -->
      <TableHeaderItem>
        <ListDisplayMenu
          columns-label="Columns…"
          :columns-shown-count="visibleColumnCount"
          columns-test-id="column-settings-button"
          :columns-additional-columns="props.additionalColumns"
          :page-memory-key="storeKey"
        />
      </TableHeaderItem>
      <TableHeaderItem>
        <!-- The span shows the tooltip while the button is disabled, since a disabled button gets no pointer events. -->
        <LazyTooltip>
          <span class="inline-flex" data-testid="history-button-tooltip-target"><HistoryButton /></span>
          <template #content>Your track history</template>
        </LazyTooltip>
      </TableHeaderItem>
    </template>

    <template #right>
      <!-- Only mount the accent Save item when there are pending edits — an
           empty w-full slot would otherwise eat the row on mobile and shove the
           pager to the right instead of letting it span the width. -->
      <TableHeaderItem v-if="rowsToProcess" class="w-full md:w-auto">
        <Button
          variant="accent"
          data-testid="track-matching-save-button"
          :disabled="saveDisabled"
          :title="saveDisabled ? saveDisabledTooltip : undefined"
          @click="$emit('processRows')"
        >
          Save matches ({{ rowsToProcess }})
        </Button>
      </TableHeaderItem>
      <!-- Fills the width on the wrapped small-screen row; compact at md+. -->
      <TableHeaderItem class="md:min-w-max md:shrink-0">
        <TableHeaderPaginationGroup
          cursor-mode
          :total-records="totalRecords"
          :current-page="currentPage"
          :has-next="hasNext"
          :has-prev="hasPrev"
          :loading="loading"
          total-label="tracks"
          @navigate="$emit('navigate', $event)"
        />
      </TableHeaderItem>
    </template>
  </TableHeader>
</template>
